import React from 'react';

interface ScenarioStepIndicatorProps {
  stepIndex: number;
  totalSteps: number;
  onJumpToStep: (i: number) => void;
}

// The dots are visited-jump-only: clicking the current dot or any earlier
// step is fine (the user knows what they're skipping back to), but jumping
// forward into a step the user hasn't reached yet would skip narrative
// setup. Forward steps render as visually-dimmed disabled buttons so the
// affordance is clear without breaking keyboard tab order.
export const ScenarioStepIndicator: React.FC<ScenarioStepIndicatorProps> = ({
  stepIndex,
  totalSteps,
  onJumpToStep,
}) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {Array.from({ length: totalSteps }, (_, i) => {
          const isActive = i === stepIndex;
          const isPast = i < stepIndex;
          const isFuture = i > stepIndex;
          const baseClass =
            'h-2 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-figma-yellow/50';
          const sizeClass = isActive ? 'w-6' : 'w-2';
          const colorClass = isActive
            ? 'bg-figma-yellow'
            : isPast
              ? 'bg-figma-yellow/40 hover:bg-figma-yellow/60'
              : 'bg-figma-lime/15 cursor-not-allowed';
          return (
            <button
              key={i}
              type="button"
              onClick={() => {
                if (isFuture) return;
                onJumpToStep(i);
              }}
              disabled={isFuture}
              aria-label={
                isFuture
                  ? `Step ${i + 1} (locked — advance with Next to unlock)`
                  : `Go to step ${i + 1}`
              }
              aria-current={isActive ? 'step' : undefined}
              aria-disabled={isFuture || undefined}
              className={`${baseClass} ${sizeClass} ${colorClass}`}
            />
          );
        })}
      </div>
      <p className="text-xs font-roboto-mono text-neutral-500 uppercase tracking-wider">
        Step {stepIndex + 1} of {totalSteps}
      </p>
    </div>
  );
};
